import { useEffect, useState } from "react";
import { useStation } from "@/context/StationContext";
import api, { apiErr } from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { FileText, Loader2, Download, Mail, Save } from "lucide-react";

const card = "rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl";

export default function Kpdn() {
  const { stationId } = useStation();
  const [email, setEmail] = useState("");
  const [month, setMonth] = useState("");
  const [saving, setSaving] = useState(false);
  const [sending, setSending] = useState(false);

  useEffect(() => {
    api.get("/kpdn/settings").then((r) => setEmail(r.data.email || "")).catch(() => {});
    api.get("/data-months").then((r) => { const m = r.data || []; setMonth(m.length ? m[m.length - 1] : new Date().toISOString().slice(0, 7)); });
  }, []);

  const saveEmail = async () => {
    setSaving(true);
    try { await api.put("/kpdn/settings", { email }); toast.success("Recipient email saved"); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSaving(false);
  };

  const download = async () => {
    try {
      const res = await api.get(`/kpdn/export?month=${month}${stationId !== "all" ? `&station_id=${stationId}` : ""}`, { responseType: "blob" });
      const u = URL.createObjectURL(res.data);
      const a = document.createElement("a"); a.href = u; a.download = `kpdn-${month}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(u);
      toast.success("KPDN report downloaded");
    } catch { toast.error("Download failed"); }
  };

  const sendNow = async () => {
    if (!email) return toast.error("Save a recipient email first");
    setSending(true);
    try { const { data } = await api.post(`/kpdn/send?month=${month}${stationId !== "all" ? `&station_id=${stationId}` : ""}`); toast.success(`Report emailed to ${data.to}`); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    setSending(false);
  };

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs font-mono uppercase tracking-widest text-emerald-400 mb-1">Compliance</div>
        <h1 className="font-[Outfit] font-bold text-3xl sm:text-4xl tracking-tight flex items-center gap-2">
          <FileText className="w-8 h-8 text-emerald-400" /> KPDN Report
        </h1>
        <p className="text-[#9CA3AF] mt-1 text-sm">Monthly daily-sales report (litres) for RON95 &amp; Diesel, split into Wed–Tue weeks (one tab per week), headed by Station Name &amp; Location. Auto-emailed on the 2nd of each month for the previous month.</p>
      </div>

      <div className={`${card} p-5 sm:p-6`}>
        <h3 className="font-[Outfit] font-semibold text-lg mb-3">Recipient email</h3>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div className="flex-1">
            <Label className="text-[#9CA3AF] text-xs">KPDN email address (saved once)</Label>
            <Input type="email" data-testid="kpdn-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="e.g. name@kpdn.gov.my" className="mt-1.5 bg-[#0B0F17] border-[#374151] text-white" />
          </div>
          <Button onClick={saveEmail} disabled={saving} data-testid="kpdn-save-email"
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />} Save
          </Button>
        </div>
      </div>

      <div className={`${card} p-5 sm:p-6`}>
        <h3 className="font-[Outfit] font-semibold text-lg mb-3">Generate / send report</h3>
        <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
          <div>
            <Label className="text-[#9CA3AF] text-xs">Report month</Label>
            <Input type="month" data-testid="kpdn-month-input" value={month} onChange={(e) => setMonth(e.target.value)}
              className="mt-1.5 bg-[#111827] border-[#374151] text-white w-44" />
          </div>
          <Button variant="outline" onClick={download} data-testid="kpdn-download-button"
            className="border-[#374151] bg-transparent hover:bg-[#1F2937] text-white">
            <Download className="w-4 h-4 mr-2" /> Download Excel (4 week tabs)
          </Button>
          <Button onClick={sendNow} disabled={sending} data-testid="kpdn-send-button"
            className="bg-emerald-500 hover:bg-emerald-400 text-black font-semibold">
            {sending ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Mail className="w-4 h-4 mr-2" />} Email now
          </Button>
        </div>
        <p className="text-xs text-[#9CA3AF] mt-3">The emailed report contains the weekly litre tables inline (per station). The Excel download has one tab per week.</p>
      </div>
    </div>
  );
}
